import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SafetyBoundaryNotice } from "@/components/safety/SafetyBoundaryNotice";
import { useAskNiq } from "@/contexts/AskNiqContext";
import { Button } from "@/components/ui/button";

const CATEGORY_COLOR = { OBSERVED: "#00C853", MODELLED: "var(--oim-text-secondary)", PROJECTED: "#007AFF", RECOMMENDED: "var(--oim-gold)" };

function getSessionId() {
  let id = localStorage.getItem("oim-askniq-session");
  if (!id) {
    id = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("oim-askniq-session", id);
  }
  return id;
}

export function AskNiqPanel({ fullPage = false }) {
  const [open, setOpen] = useState(fullPage);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const lastTriggerTs = useRef(null);
  const sessionId = getSessionId();
  const askniq = useAskNiq();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const askQuestion = async (q, meta) => {
    if (!q.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/askniq/ask", { question: q, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", ...data, meta }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", answer: "Ask Niq could not reach the grounding service. Please try again.", statements: [], meta }]);
    } finally {
      setLoading(false);
    }
  };

  const ask = async () => {
    if (!question.trim() || loading) return;
    const q = question;
    setQuestion("");
    await askQuestion(q, null);
  };

  useEffect(() => {
    if (askniq?.trigger && askniq.trigger.ts !== lastTriggerTs.current) {
      lastTriggerTs.current = askniq.trigger.ts;
      setOpen(true);
      askQuestion(askniq.trigger.question, askniq.trigger.meta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askniq?.trigger]);

  const createActionFromFault = async (m, idx) => {
    try {
      await api.post("/actions", {
        title: `Resolve: ${m.meta.label}`,
        rationale: `${m.meta.root_cause ? `Root cause: ${m.meta.root_cause}. ` : ""}Ask Niq: ${m.answer}`,
        scenario_key: m.meta.scenario_key,
        source: "askniq",
      });
      setMessages((msgs) => msgs.map((msg, i) => (i === idx ? { ...msg, actionCreated: true } : msg)));
      toast.success("Action created — visible on the Actions & M&V page");
    } catch (e) {
      toast.error("Could not create action. Please try again.");
    }
  };

  const panel = (
    <div
      className={fullPage ? "w-full" : "fixed bottom-20 right-6 w-[380px] max-h-[560px] z-50 rounded-xl border shadow-2xl flex flex-col"}
      style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}
      data-testid="askniq-panel"
    >
      {!fullPage && (
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--oim-border)" }}>
          <span className="text-sm font-heading font-bold text-[var(--oim-text)]">Ask Niq</span>
          <button onClick={() => setOpen(false)} data-testid="askniq-close-button"><X className="w-4 h-4 text-[var(--oim-text-muted)]" /></button>
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: fullPage ? "60vh" : "380px" }}>
        {messages.length === 0 && (
          <div className="text-xs text-[var(--oim-text-muted)]" data-testid="askniq-empty">
            Ask a grounded question about current plant state — e.g. "What is limiting throughput right now?"
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`askniq-message-${i}`}>
            {m.role === "user" ? (
              <div className="text-sm text-[var(--oim-text)] font-medium">{m.content}</div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-[var(--oim-text-secondary)]">{m.answer}</div>
                {m.safety_related && <SafetyBoundaryNotice />}
                {(m.statements || []).map((s, si) => (
                  <div key={si} className="flex items-center gap-2 text-[10px]" data-testid={`askniq-source-${i}-${si}`}>
                    <span className="font-heading uppercase px-1.5 py-0.5 rounded-full border" style={{ borderColor: CATEGORY_COLOR[s.category] || "var(--oim-border)", color: CATEGORY_COLOR[s.category] || "var(--oim-text-muted)" }}>
                      {s.category}
                    </span>
                    <span className="text-[var(--oim-text-muted)]">{s.text}</span>
                    {s.value != null && <span className="font-mono-data text-[var(--oim-text)]">{s.value}</span>}
                  </div>
                ))}
                {m.meta?.scenario_key && (
                  m.actionCreated ? (
                    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--oim-green)" }} data-testid={`askniq-action-created-${i}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Action created
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-[#00C853] text-black hover:bg-[#00a844] rounded-full text-xs"
                      onClick={() => createActionFromFault(m, i)}
                      data-testid={`askniq-create-action-${i}`}
                    >
                      Create Action
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        ))}
        {loading && <div className="text-xs text-[var(--oim-text-dim)]" data-testid="askniq-loading">Grounding response…</div>}
      </div>
      <div className="flex items-center gap-2 p-3 border-t" style={{ borderColor: "var(--oim-border)" }}>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ask a grounded question…"
          className="flex-1 bg-transparent text-sm outline-none text-[var(--oim-text)] placeholder:text-[var(--oim-text-dim)]"
          data-testid="askniq-question-input"
        />
        <button onClick={ask} data-testid="askniq-send-button"><Send className="w-4 h-4 text-[var(--oim-green)]" /></button>
      </div>
    </div>
  );

  if (fullPage) return panel;

  return (
    <>
      {open && panel}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full flex items-center justify-center shadow-2xl z-50"
        style={{ backgroundColor: "#00C853" }}
        data-testid="askniq-fab-button"
      >
        <MessageCircle className="w-5 h-5 text-black" />
      </button>
    </>
  );
}

